<template>
  <div class="content-builder animation-layout">
    <div class="form-group">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 class="mb-1 font-weight-bold">Add Timeline Events</h5>
          <p class="text-muted mb-0">Timeline events table</p>
        </div>
        <div class="total-duration-display" v-if="totalDuration > 0">
          <span class="small text-muted font-weight-bold mr-1">
            <i class="fa fa-clock-o mr-1"></i> Total Duration:
          </span>
          <span class="badge badge-light border text-muted font-weight-normal">
            {{ formatDuration(totalDuration) }}
          </span>
        </div>
      </div>

      <div class="timeline-events-container">
        <timeline-events-table
            :events="timelineEvents"
            @update:events="timelineEvents = $event"
            @set-media-type="setMediaType"
            @format-time="formatTimeInput"
            @until-end-change="onUntilEndChange"
            @update-field="updateEventField"
            @remove="removeEvent"
            @upload-vtt="handleEventVttUpload"
            @clear-vtt="clearEventVtt"
            @format-caption-time="formatCaptionTime"
            @update-caption="updateCaption"
            @remove-caption="removeCaption"
            @add-caption="addCaption"
            @update-captions="updateEventCaptions"
        />

        <div class="mt-2">
          <button
              type="button"
              @click="addEvent"
              class="btn btn-primary"
              :disabled="!canAddNewEvent"
              :title="!canAddNewEvent ? 'Please complete the current event (Media, Start Time, End Time) before adding a new one' : ''"
          >
            <i class="fa fa-plus"></i> Add
          </button>
          <small v-if="!canAddNewEvent" class="text-danger ml-2">
            Complete the current event (Media, Start Time, End Time) before adding a new one
          </small>
        </div>
      </div>
    </div>

    <closed-captions-section
        mode="import"
        :events="vttPreview"
        :import-options="{
          sectionVisible: showVttSection,
          fileName: vttFileName,
          fileInputId: 'vttFileInput'
        }"
        :add-cue-options="{ disabled: !canAddNewVttCue }"
        @toggle-section="toggleVttSection"
        @file-selected="handleVttFileSelected"
        @update:events="vttPreview = $event"
        @format-time="formatVttTimeInput"
        @update-field="updateVttField"
        @remove="removeVttCue"
        @add-cue="addVttCue"
        @clear="clearVttPreview"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, provide } from 'vue'
import { get } from 'lodash'
import { v4 as uuid4 } from 'uuid'
import TimelineEventsTable from '../TimelineEventsTable.vue'
import ClosedCaptionsSection from '../TimelineClosedCaptionsSection.vue'
import { useVttUpload } from '../../../common/composables/useVttUpload'
import {
  EasingIn,
  EasingOut,
  MediaType
} from '../../../common/animation-constants'
import {
  timeToSeconds,
  formatDuration,
  formatTimeInput as formatTimeInputUtil
} from '../../../common/time-utils'

const { parseVttFile: parseVttFileFromComposable } = useVttUpload()

const props = defineProps({
  source: {
    type: Object,
    default: () => ({})
  },
  pageId: {
    type: [String, Number],
    default: null
  }
})

const createDefaultTimelineEvent = () => ({
  id: uuid4(),
  mediaType: MediaType.IMAGE,
  mediaUrl: '',
  textContent: '',
  htmlContent: '',
  perMediaCaptions: [],
  groupId: '',
  startTime: '',
  endTime: '',
  untilEnd: false,
  easingIn: EasingIn.NONE,
  easingOut: EasingOut.NONE
})

const rawTimelineEvents = get(props.source, 'timelineEvents', [])
const timelineEvents = ref(
  rawTimelineEvents.length > 0
    ? rawTimelineEvents.map(event => ({
        ...createDefaultTimelineEvent(),
        ...event,
        id: event.id || uuid4()
      }))
    : []
)

const rawClosedCaptions = get(props.source, 'closedCaptions', [])
const vttPreview = ref(
  rawClosedCaptions.length > 0
    ? rawClosedCaptions.map(caption => ({
        ...caption,
        id: caption.id || uuid4()
      }))
    : []
)

const showVttSection = ref(false)
const vttFileName = ref('')

const canAddNewEvent = computed(() => {
  if (timelineEvents.value.length === 0) {
    return true
  }

  const lastEvent = timelineEvents.value[timelineEvents.value.length - 1]
  return isEventValid(lastEvent)
})

const canAddNewVttCue = computed(() => {
  if (vttPreview.value.length === 0) {
    return true
  }

  const lastCue = vttPreview.value[vttPreview.value.length - 1]
  const hasText = !!lastCue.textContent && lastCue.textContent.trim() !== ''
  const hasStartTime = !!lastCue.startTime && lastCue.startTime.trim() !== ''
  const hasEndTime = !!lastCue.endTime && lastCue.endTime.trim() !== ''
  return hasText && hasStartTime && hasEndTime
})

const totalDuration = computed(() => {
  if (timelineEvents.value.length === 0) {
    return 0
  }

  let maxEndTime = 0
  timelineEvents.value.forEach(event => {
    if (event.untilEnd) {
      return
    }
    const startTime = timeToSeconds(event.startTime)
    const endTime = timeToSeconds(event.endTime)
    if (startTime >= 0 && endTime >= 0) {
      maxEndTime = Math.max(maxEndTime, endTime)
    }
  })

  return maxEndTime
})

function isEventValid(event) {
  let hasMedia
  if (event.mediaType === MediaType.TEXT) {
    hasMedia = !!event.textContent && event.textContent.trim() !== ''
  } else if (event.mediaType === MediaType.HTML) {
    hasMedia = !!event.htmlContent && event.htmlContent.trim() !== ''
  } else {
    hasMedia = !!event.mediaUrl && event.mediaUrl.trim() !== ''
  }
  const hasStartTime = !!event.startTime && event.startTime.trim() !== ''
  const hasEndTime = event.untilEnd || (!!event.endTime && event.endTime.trim() !== '')
  return hasMedia && hasStartTime && hasEndTime
}

function addEvent() {
  if (timelineEvents.value.length > 0) {
    const lastEvent = timelineEvents.value[timelineEvents.value.length - 1]

    if (!isEventValid(lastEvent)) {
      const missingFields = []
      if (lastEvent.mediaType === MediaType.TEXT) {
        if (!lastEvent.textContent || lastEvent.textContent.trim() === '') {
          missingFields.push('Media (Text)')
        }
      } else if (lastEvent.mediaType === MediaType.HTML) {
        if (!lastEvent.htmlContent || lastEvent.htmlContent.trim() === '') {
          missingFields.push('Media (HTML)')
        }
      } else {
        if (!lastEvent.mediaUrl || lastEvent.mediaUrl.trim() === '') {
          missingFields.push('Media')
        }
      }
      if (!lastEvent.startTime || lastEvent.startTime.trim() === '') {
        missingFields.push('Start Time')
      }
      if (!lastEvent.untilEnd && (!lastEvent.endTime || lastEvent.endTime.trim() === '')) {
        missingFields.push('End Time (or check "Until End")')
      }
      alert(`Please complete the current event before adding a new one. Missing: ${missingFields.join(', ')}`)
      return
    }
  }

  const newEvent = createDefaultTimelineEvent()
  timelineEvents.value.push(newEvent)
}

function removeEvent(index) {
  if (confirm('Are you sure you want to delete this timeline event?')) {
    timelineEvents.value.splice(index, 1)
  }
}

function setMediaType(index, type) {
  const event = timelineEvents.value[index]
  event.mediaType = type
  if (type === MediaType.TEXT) {
    event.mediaUrl = ''
    event.htmlContent = ''
  } else if (type === MediaType.HTML) {
    event.mediaUrl = ''
    event.textContent = ''
  } else {
    event.textContent = ''
    event.htmlContent = ''
  }
}

function onUntilEndChange(index) {
  if (timelineEvents.value[index].untilEnd) {
    timelineEvents.value[index].endTime = ''
  }
}

function updateEventField(index, field, value) {
  if (timelineEvents.value[index]) {
    timelineEvents.value[index][field] = value
  }
}

function updateVttField(index, field, value) {
  if (vttPreview.value[index]) {
    vttPreview.value[index][field] = value
  }
}

function formatTimeInput(index, field) {
  const value = timelineEvents.value[index][field]
  const formatted = formatTimeInputUtil(value)
  if (formatted !== null) {
    timelineEvents.value[index][field] = formatted
  }
}

function toggleVttSection() {
  showVttSection.value = !showVttSection.value
}

async function handleVttFileSelected(file) {
  if (!file) {
    return
  }

  try {
    // Use composable's parseVttFile which handles validation and file reading
    const parsedCues = await parseVttFileFromComposable(file, {
      mode: 'import',
      MediaType,
      EasingIn,
      EasingOut
    })

    vttFileName.value = file.name
    vttPreview.value = parsedCues

    if (parsedCues.length === 0) {
      alert('No valid cues found in the VTT file. Please check the file format.')
      clearVttPreview()
    }
  } catch (error) {
    alert(`Failed to parse VTT file: ${error.message}`)
    clearVttPreview()
  }
}

function formatVttTimeInput(index, field) {
  const value = vttPreview.value[index][field]
  const formatted = formatTimeInputUtil(value)
  if (formatted !== null) {
    vttPreview.value[index][field] = formatted
  }
}

function addVttCue() {
  if (vttPreview.value.length > 0) {
    const lastCue = vttPreview.value[vttPreview.value.length - 1]

    if (!canAddNewVttCue.value) {
      const missingFields = []
      if (!lastCue.textContent || lastCue.textContent.trim() === '') {
        missingFields.push('Text')
      }
      if (!lastCue.startTime || lastCue.startTime.trim() === '') {
        missingFields.push('Start Time')
      }
      if (!lastCue.endTime || lastCue.endTime.trim() === '') {
        missingFields.push('End Time')
      }
      alert(`Please complete the current cue before adding a new one. Missing: ${missingFields.join(', ')}`)
      return
    }
  }

  const newCue = {
    id: uuid4(),
    mediaType: MediaType.TEXT,
    mediaUrl: '',
    textContent: '',
    startTime: '',
    endTime: '',
    untilEnd: false,
    groupId: '',
    easingIn: EasingIn.NONE,
    easingOut: EasingOut.NONE
  }
  vttPreview.value.push(newCue)
}

function removeVttCue(index) {
  if (confirm('Are you sure you want to delete this cue?')) {
    vttPreview.value.splice(index, 1)
  }
}

function clearVttPreview() {
  if (confirm('Are you sure you want to clear all closed captions?')) {
    vttPreview.value = []
    vttFileName.value = ''
  }
}

async function handleEventVttUpload(index) {
  const event = timelineEvents.value[index]
  if (!event || (event.mediaType !== MediaType.MP3 && event.mediaType !== MediaType.MP4)) {
    return
  }

  const fileInput = document.createElement('input')
  fileInput.type = 'file'
  fileInput.accept = '.vtt'

  fileInput.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }

    try {
      // Use composable's parseVttFile which handles validation and file reading
      const parsedCues = await parseVttFileFromComposable(file, {
        mode: 'per-event'
      })

      if (parsedCues.length === 0) {
        alert('No valid cues found in the VTT file.')
        return
      }

      timelineEvents.value[index].perMediaCaptions = parsedCues
    } catch (error) {
      alert(`Failed to parse VTT file: ${error.message}`)
    }
  }

  fileInput.click()
}

function clearEventVtt(index) {
  const event = timelineEvents.value[index]
  if (!event) {
    return
  }

  if (confirm('Are you sure you want to clear the captions for this media?')) {
    timelineEvents.value[index].perMediaCaptions = []
  }
}

function formatCaptionTime(eventIndex, captionIndex, field) {
  const event = timelineEvents.value[eventIndex]
  if (!event || !event.perMediaCaptions || !event.perMediaCaptions[captionIndex]) {
    return
  }

  const value = event.perMediaCaptions[captionIndex][field]
  const formatted = formatTimeInputUtil(value)
  if (formatted !== null) {
    event.perMediaCaptions[captionIndex][field] = formatted
  }
}

function updateCaption(eventIndex, captionIndex, field, value) {
  const event = timelineEvents.value[eventIndex]
  if (!event || !event.perMediaCaptions || !event.perMediaCaptions[captionIndex]) {
    return
  }

  event.perMediaCaptions[captionIndex][field] = value
}

function removeCaption(eventIndex, captionIndex) {
  const event = timelineEvents.value[eventIndex]
  if (!event || !event.perMediaCaptions) {
    return
  }

  if (confirm('Are you sure you want to delete this caption?')) {
    event.perMediaCaptions.splice(captionIndex, 1)
  }
}

function canAddNewCaption(eventIndex) {
  const event = timelineEvents.value[eventIndex]
  if (!event || !event.perMediaCaptions || event.perMediaCaptions.length === 0) {
    return true
  }

  const lastCaption = event.perMediaCaptions[event.perMediaCaptions.length - 1]
  const hasText = !!lastCaption.textContent && lastCaption.textContent.trim() !== ''
  const hasStartTime = !!lastCaption.startTime && lastCaption.startTime.trim() !== ''
  const hasEndTime = !!lastCaption.endTime && lastCaption.endTime.trim() !== ''
  return hasText && hasStartTime && hasEndTime
}

provide('captionValidation', {
  canAddCaption: canAddNewCaption
})

function addCaption(eventIndex) {
  const event = timelineEvents.value[eventIndex]
  if (!event) {
    return
  }

  if (!event.perMediaCaptions) {
    event.perMediaCaptions = []
  }

  if (event.perMediaCaptions.length > 0 && !canAddNewCaption(eventIndex)) {
    const lastCaption = event.perMediaCaptions[event.perMediaCaptions.length - 1]
    const missingFields = []
    if (!lastCaption.textContent || lastCaption.textContent.trim() === '') {
      missingFields.push('Text')
    }
    if (!lastCaption.startTime || lastCaption.startTime.trim() === '') {
      missingFields.push('Start Time')
    }
    if (!lastCaption.endTime || lastCaption.endTime.trim() === '') {
      missingFields.push('End Time')
    }
    alert(`Please complete the current caption before adding a new one. Missing: ${missingFields.join(', ')}`)
    return
  }

  event.perMediaCaptions.push({
    id: uuid4(),
    textContent: '',
    startTime: '',
    endTime: '',
    easingIn: EasingIn.NONE,
    easingOut: EasingOut.NONE
  })
}

function updateEventCaptions(eventIndex, captions) {
  const event = timelineEvents.value[eventIndex]
  if (!event) {
    return
  }

  event.perMediaCaptions = captions
}

function getValue() {
  return {
    timelineEvents: timelineEvents.value,
    closedCaptions: vttPreview.value
  }
}

function setup() {
  if (typeof $ !== 'undefined' && $.fn && $.fn.filemanager) {
    $('[data-lfm="file"]').filemanager('file', { prefix: '/filemanager' })
    $('[data-lfm="image"]').filemanager('image', { prefix: '/filemanager' })
  }
}

onMounted(() => {
  setup()
})

defineExpose({
  getValue,
  setup
})
</script>

<style lang="scss" scoped>
.animation-layout {
  .timeline-events-container {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    overflow: visible;
    position: relative;
  }

  .total-duration-display {
    .badge {
      font-size: 0.9rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      i {
        font-size: 1rem;
      }
    }
  }
}

</style>